import type {
  AutomationSafetyDiagnosticPage,
  AutomationTopologyState,
} from '@streamlain/automation-safety-contract';

export type AutomationSafetyPresentation = {
  severity: 'neutral' | 'warning' | 'blocking';
  titleKey:
    | 'com.streamlain.automation-safety.draft'
    | 'com.streamlain.automation-safety.pending'
    | 'com.streamlain.automation-safety.safe'
    | 'com.streamlain.automation-safety.blocked'
    | 'com.streamlain.automation-safety.external'
    | 'com.streamlain.automation-safety.failed';
  enableAllowed: boolean;
};

export function presentAutomationSafety(
  state: AutomationTopologyState,
  actions: AutomationSafetyDiagnosticPage['actions']
): AutomationSafetyPresentation {
  switch (state) {
    case 'DRAFT':
      return {
        severity: 'neutral',
        titleKey: 'com.streamlain.automation-safety.draft',
        enableAllowed: false,
      };
    case 'CANDIDATE_PENDING':
      return {
        severity: 'neutral',
        titleKey: 'com.streamlain.automation-safety.pending',
        enableAllowed: false,
      };
    case 'SAFE':
      return {
        severity: 'neutral',
        titleKey: 'com.streamlain.automation-safety.safe',
        enableAllowed: actions.includes('enable'),
      };
    case 'BLOCKED_CYCLE':
      return {
        severity: 'blocking',
        titleKey: 'com.streamlain.automation-safety.blocked',
        enableAllowed: false,
      };
    case 'EXTERNAL_UNVERIFIABLE':
      return {
        severity: 'warning',
        titleKey: 'com.streamlain.automation-safety.external',
        enableAllowed: actions.includes('enable'),
      };
    case 'ANALYSIS_FAILED':
      return {
        severity: 'blocking',
        titleKey: 'com.streamlain.automation-safety.failed',
        enableAllowed: false,
      };
    default: {
      const exhaustive: never = state;
      return exhaustive;
    }
  }
}

export function isCurrentAutomationSafety(
  page: AutomationSafetyDiagnosticPage,
  workflowId: string,
  configId: string
): boolean {
  return (
    page.subject.workflowId === workflowId && page.subject.configId === configId
  );
}

type AutomationSafetyWaitOptions = {
  timeoutMs?: number;
  intervalMs?: number;
  now?: () => number;
  delay?: (milliseconds: number) => Promise<void>;
};

export async function waitForCurrentAutomationSafety(
  load: () => Promise<AutomationSafetyDiagnosticPage | null>,
  workflowId: string,
  configId: string,
  options: AutomationSafetyWaitOptions = {}
): Promise<AutomationSafetyDiagnosticPage | null> {
  const timeoutMs = options.timeoutMs ?? 15_000;
  const intervalMs = options.intervalMs ?? 500;
  const now = options.now ?? Date.now;
  const delay =
    options.delay ??
    ((milliseconds: number) =>
      new Promise(resolve => globalThis.setTimeout(resolve, milliseconds)));
  const deadline = now() + timeoutMs;
  let latest: AutomationSafetyDiagnosticPage | null = null;

  while (true) {
    const result = await load();
    if (result && isCurrentAutomationSafety(result, workflowId, configId)) {
      latest = result;
      if (result.topology.state !== 'CANDIDATE_PENDING') {
        return result;
      }
    }

    const remaining = deadline - now();
    if (remaining <= 0) return latest;
    await delay(Math.min(intervalMs, remaining));
  }
}

export function automationSafetyEnableFailureKey(
  error: unknown
):
  | 'com.streamlain.automation-safety.enable-failed'
  | 'com.streamlain.automation-safety.reconfigure' {
  const message = error instanceof Error ? error.message : String(error);
  return message.includes('AUTOMATION_HTTP_TARGET_RECONFIGURE')
    ? 'com.streamlain.automation-safety.reconfigure'
    : 'com.streamlain.automation-safety.enable-failed';
}
